import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import dayjs from 'dayjs'
import { metodoPrograma } from '../../../../components/api'

const processarItem = (item) => ({
  ...item,
  idx: Symbol('idx ' + item?.idProduto + item?.tpProduto),
  qtProdutoAutorizado: item?.qtProduto || 0,
  vlUnitarioInicial: item?.vlUnitario || 0,
  vlTotal: item?.vlUnitario * (item?.qtProduto || 1) || 0,
})

export const getCalculosOrcamento = (
  values,
  listaTerapias,
  listaProdutos,
  listaProdutosAdicionais,
  valorTotalTabela,
) => {
  const saldoProposta =
    (values.vlSubTotal || 0) -
    (values.vlPago || 0) -
    (values.vlDescontoGeral || 0)

  const totalPedidosEmAberto =
    (valorTotalTabela.terapias || 0) + (valorTotalTabela.produtos || 0)
  const valorTotalTabelaBase =
    // O saldo recebido do backend já inclui os itens registrados no check-in
    // e no PDV. Apenas vendas novas do checkout ainda não compõem esse saldo.
    saldoProposta +
    (valorTotalTabela.adicionaisIncluidosNoSaldo
      ? 0
      : valorTotalTabela.adicionais || 0)

  const totalGeral = valorTotalTabelaBase

  const totalDevedor =
    valorTotalTabelaBase -
    (values.vlDesconto || 0) -
    values.formasPagamento?.reduce(
      (acc, curr) => acc + Number(curr.vlPago || 0),
      0,
    )

  const tratarLista = (lista, qtField = 'qtProdutoAutorizado') =>
    lista.map((item) => ({
      nome: item.dsProduto,
      paciente: item.nmPaciente || '',
      qtd: item[qtField] || 0,
      unitario: item.vlUnitario || 0,
      valor: item.vlTotal || 0,
      snPago: item.snPago || 'N',
    }))

  return {
    saldoProposta,
    valorTotalTabelaBase,
    totalPedidosEmAberto,
    totalGeral,
    totalDevedor,
    listaTerapiasTratado: tratarLista(listaTerapias),
    listaProdutosTratado: tratarLista(listaProdutos),
    listaProdutosAddTratado: tratarLista(listaProdutosAdicionais, 'qtProduto'),
    subTotalTerapias: valorTotalTabela.terapias || 0,
    subTotalProdutos: valorTotalTabela.produtos || 0,
    subTotalAdicionais: valorTotalTabela.adicionais || 0,
  }
}

export const calculateIdade = (dataNasc) => {
  if (!dataNasc) return ''
  const hoje = dayjs()
  let nasc = dayjs(dataNasc)
  if (!nasc.isValid()) return ''

  const anos = hoje.diff(nasc, 'year')
  nasc = nasc.add(anos, 'year')
  const meses = hoje.diff(nasc, 'month')
  if (meses < 0) return `${anos} anos`
  nasc = nasc.add(meses, 'month')
  const dias = hoje.diff(nasc, 'day')

  return `${anos} anos, ${meses} meses e ${dias} dias`
}

const buildInitialValues = (linha, lojaPontoVenda, isModal) => {
  const formasPgto = (linha?.[11] || []).map((item) => [
    [item?.[0]?.[0], crypto.randomUUID()],
    ...item.slice(1),
  ])

  const acFormaPagamento =
    formasPgto?.length > 0
      ? formasPgto.map((item) => [item[0], item[1], item[4], item[3], item[2]])
      : []
  const formasPagamento =
    formasPgto?.length > 0
      ? formasPgto.map((item) => ({
          idFormaPagamento: item[0][0],
          vlPago: item[4],
          qtParcelas: item[2],
          isParcelas: item[3],
          unic: item[0][1],
        }))
      : []

  // O pagamento do próprio checkout já será abatido por formasPagamento.
  const pagamentoCheckout =
    linha?.[2] === '9'
      ? formasPagamento.reduce(
          (total, forma) => total + Number(forma.vlPago || 0),
          0,
        )
      : 0

  return {
    idAtendimento: linha?.[0],
    idPaciente: linha?.[1]?.[0]?.[0] || '',
    dsPaciente: linha?.[1]?.[1] || '',
    nrCpf: linha?.[1]?.[2] || '',
    dtNascimento: linha?.[1]?.[3] ? dayjs(linha?.[1]?.[3]) : '',
    nrTelefone: linha?.[1]?.[4] || '',
    dsQuarto: linha?.[12] || '',
    dsObservacoes: linha?.[13] || '',
    vlPago: isModal
      ? Number(linha?.[12] || 0)
      : Number(linha?.[8] || 0) - pagamentoCheckout,
    dtInicio: linha?.[5] ? dayjs(linha?.[5]) : null,
    dtFim: linha?.[6] ? dayjs(linha?.[6]) : null,
    idPontoVenda: lojaPontoVenda?.idPontoVenda,
    vlSubTotal: Number(linha?.[10] || 0),
    vlCaucao: Number(linha?.[4] || 0),
    vlDesconto: isModal ? Number(linha?.[9] || 0) : 0,
    vlDescontoGeral: Number(linha?.[9] || 0),
    acFormaPagamento,
    formasPagamento,
    acProduto: null,
  }
}

const CheckoutContext = createContext(null)

export const useCheckout = () => {
  const ctx = useContext(CheckoutContext)
  return ctx
}

export const CheckoutProvider = ({
  children,
  linha,
  lojaPontoVenda,
  isModal,
}) => {
  const [listaTerapias, setListaTerapias] = useState([])
  const [listaProdutos, setListaProdutos] = useState([])
  const [listaProdutosAdicionais, setListaProdutosAdicionais] = useState([])
  const [pedidosPdvEmAberto, setPedidosPdvEmAberto] = useState([])
  const [dependentes, setDependentes] = useState([])
  const [initialValues, setInitialValues] = useState({})
  const [loading, setLoading] = useState(true)

  const isFinalizado = linha?.[2] === '9'
  const idAtendimento = linha?.[0]

  useEffect(() => {
    if (!linha) return

    metodoPrograma('getItensCheckout', {
      idAtendimento: linha[0],
    }).then((resp) => {
      const itensRascunhoVenda = [
        ...(resp?.listaProdutos || []),
        ...(resp?.listaTerapias || []),
      ].filter(
        (item) => String(item?.tpPedido) === '2' && item?.tpOrigem === 'V',
      )
      const produtoOuTerapiaDoRascunho = (item) =>
        String(item?.tpPedido) === '2' && item?.tpOrigem === 'V'
      const produtosProcessados = (resp?.listaProdutos || [])
        .filter((item) => !produtoOuTerapiaDoRascunho(item))
        .map(processarItem)
      const terapiasProcessadas = (resp?.listaTerapias || [])
        .filter((item) => !produtoOuTerapiaDoRascunho(item))
        .map(processarItem)

      setListaProdutos(produtosProcessados)
      setListaTerapias(terapiasProcessadas)
      setListaProdutosAdicionais(itensRascunhoVenda.map(processarItem))
      setPedidosPdvEmAberto(
        (resp?.listaPedidosPdvEmAberto || []).map(processarItem),
      )
      setDependentes(linha?.[7] || [])
      setInitialValues(buildInitialValues(linha, lojaPontoVenda, isModal))
      setLoading(false)
    })
  }, [linha, lojaPontoVenda, isModal])

  const terapiasEmAberto = useMemo(
    () => listaTerapias.filter((i) => i.snPago !== 'S'),
    [listaTerapias],
  )

  const produtosEmAberto = useMemo(
    () => listaProdutos.filter((i) => i.snPago !== 'S'),
    [listaProdutos],
  )

  const terapiasPagas = useMemo(
    () => listaTerapias.filter((i) => i.snPago === 'S'),
    [listaTerapias],
  )

  const produtosPagos = useMemo(
    () => listaProdutos.filter((i) => i.snPago === 'S'),
    [listaProdutos],
  )

  const itensPdv = useMemo(
    () =>
      [...listaProdutos, ...listaTerapias]?.filter(
        (pedido) => pedido?.tpPedido === '0',
      ),
    [listaProdutos, listaTerapias],
  )

  const valorProdutosAdicionaisFinalizados = useMemo(
    () =>
      isFinalizado
        ? [...listaProdutos, ...listaTerapias]
            .filter(
              (item) => item.tpOrigem === 'V' && String(item.tpPedido) === '2',
            )
            .reduce((acc, curr) => acc + Number(curr.vlTotal || 0), 0)
        : 0,
    [isFinalizado, listaProdutos, listaTerapias],
  )

  const valorTotalTabela = useMemo(
    () => ({
      terapias: terapiasEmAberto.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      produtos: produtosEmAberto.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      adicionaisIncluidosNoSaldo: isFinalizado,
      adicionais:
        listaProdutosAdicionais.reduce(
          (acc, curr) => acc + Number(curr.vlTotal || 0),
          0,
        ) + valorProdutosAdicionaisFinalizados,
    }),
    [
      terapiasEmAberto,
      produtosEmAberto,
      listaProdutosAdicionais,
      valorProdutosAdicionaisFinalizados,
      isFinalizado,
    ],
  )

  const vlAbertoPdv = itensPdv
    .filter((v) => v.snPago === 'N')
    .reduce((acc, curr) => acc + Number(curr.vlTotal || 0), 0)

  const vlPagoPdv = itensPdv
    .filter((v) => v.snPago === 'S')
    .reduce((acc, curr) => acc + Number(curr.vlTotal || 0), 0)

  const updateTerapia = useCallback((idx, updates) => {
    setListaTerapias((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const updateProduto = useCallback((idx, updates) => {
    setListaProdutos((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const updateProdutoAdicional = useCallback((idx, updates) => {
    setListaProdutosAdicionais((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const addProdutoAdicional = useCallback((produto) => {
    const idx = Symbol('idx ' + produto?.[1])
    const produtoTratado = {
      idx,
      idProduto: produto?.[0]?.[0],
      tpProduto: produto?.[0]?.[1],
      dsProduto: produto?.[1],
      qtProduto: 1,
      qtProdutoAutorizado: 1,
      vlUnitario: produto?.[2],
      vlUnitarioInicial: produto?.[2],
      vlTotal: produto?.[2],
      qtEstoque: produto?.[3] || 1,
      tpOrigem: 'V',
    }
    setListaProdutosAdicionais((prev) => [...prev, produtoTratado])
  }, [])

  const removeProdutoAdicional = useCallback((idx) => {
    setListaProdutosAdicionais((prev) =>
      prev.filter((item) => item.idx !== idx),
    )
  }, [])

  const getCalculos = useCallback(
    (values) =>
      getCalculosOrcamento(
        values,
        listaTerapias,
        listaProdutos,
        listaProdutosAdicionais,
        valorTotalTabela,
      ),
    [listaTerapias, listaProdutos, listaProdutosAdicionais, valorTotalTabela],
  )

  const value = useMemo(
    () => ({
      linha,
      isFinalizado,
      idAtendimento,
      loading,
      initialValues,
      dependentes,
      listaTerapias,
      listaProdutos,
      listaProdutosAdicionais,
      pedidosPdvEmAberto,
      terapiasEmAberto,
      produtosEmAberto,
      terapiasPagas,
      produtosPagos,
      valorTotalTabela,
      updateTerapia,
      updateProduto,
      updateProdutoAdicional,
      addProdutoAdicional,
      removeProdutoAdicional,
      getCalculos,
      vlAbertoPdv,
      vlPagoPdv,
    }),
    [
      linha,
      isFinalizado,
      idAtendimento,
      loading,
      initialValues,
      dependentes,
      listaTerapias,
      listaProdutos,
      listaProdutosAdicionais,
      pedidosPdvEmAberto,
      terapiasEmAberto,
      produtosEmAberto,
      terapiasPagas,
      produtosPagos,
      valorTotalTabela,
      updateTerapia,
      updateProduto,
      updateProdutoAdicional,
      addProdutoAdicional,
      removeProdutoAdicional,
      getCalculos,
      vlAbertoPdv,
      vlPagoPdv,
    ],
  )

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  )
}
